import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Code2, MessageSquareCode, Sparkles, Loader2, ArrowRight } from 'lucide-react';
import SEO, { buildBreadcrumbJsonLd } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = { Code2, MessageSquareCode, Sparkles };

const Guides = () => {
  const [magnets, setMagnets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/magnets`);
        if (!cancelled) setMagnets(res.data.magnets || []);
      } catch {
        if (!cancelled) setMagnets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen" data-testid="guides-page">
      <SEO
        title="Free Guides — Claude Code Tips, ChatGPT Prompting Patterns & Hot AI Models"
        description="Free downloadable guides for curious learners: Claude Code tips, ChatGPT prompting patterns, and the hottest AI models worth trying right now. Subscribe (free) to unlock."
        path="/guides"
        keywords={['Claude Code tips','ChatGPT prompting','AI prompt patterns','best AI models','AI guides for students','free AI guides']}
        jsonLd={buildBreadcrumbJsonLd([{ name: 'Home', url: '/' }, { name: 'Guides', url: '/guides' }])}
      />
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-semibold tracking-wider uppercase mb-4">Free · Updated weekly</span>
          <h1 className="text-5xl md:text-6xl font-light mb-5 tracking-tight">Free AI guides</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Practical, copy-paste guides for the tools every curious builder is using right now. No fluff. Subscribe once (free) to unlock all of them.
          </p>
        </div>
      </section>

      <section className="pb-32 px-6">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {magnets.map((m) => {
                const Icon = ICONS[m.icon] || Sparkles;
                return (
                  <Link
                    to={`/guides/${m.slug}`}
                    key={m.slug}
                    className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-xl"
                    data-testid={`magnet-card-${m.slug}`}
                  >
                    <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gradient-to-br ${m.color} opacity-10 group-hover:opacity-20 transition-opacity`} />
                    <div className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${m.color} text-white mb-5`}>
                      <Icon size={22} />
                    </div>
                    <h3 className="relative text-xl font-medium text-gray-900 mb-2 leading-snug">{m.title}</h3>
                    <p className="relative text-sm text-gray-600 leading-relaxed mb-5">{m.subtitle}</p>
                    <span className="relative inline-flex items-center gap-1 text-sm font-medium text-gray-900">
                      Unlock free <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Guides;
